import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Coins, Crown, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VIP_BY_TIER } from "@/lib/vipTiers";
import VipArtwork from "@/components/vip/VipArtwork";
import VipProgress from "@/components/vip/VipProgress";
import VipTierCard from "@/components/vip/VipTierCard";
import VipRules from "@/components/vip/VipRules";
import VipCashoutInfo from "@/components/vip/VipCashoutInfo";

export default function VipBadgesPage() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/vip/tiers").then(({ data }) => setTiers(data)).catch(() => setTiers({ error: true }));
    if (user) api.get("/vip/me").then(({ data }) => setMe(data)).catch(() => {});
  }, [user]);

  if (!tiers) return <div className="p-8 flex items-center gap-2 text-zinc-500 text-sm" data-testid="vip-loading"><Loader2 size={16} className="animate-spin" /> Loading VIP &amp; Badges…</div>;
  const rows = (tiers.tiers || []).map((t) => ({ ...t, ...VIP_BY_TIER[t.tier] }));
  const curRank = me?.tier ? rows.find((r) => r.tier === me.tier)?.rank : 0;

  return (
    <div className="p-6 lg:p-8 max-w-5xl space-y-8" data-testid="vip-page">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-2 flex items-center gap-2"><Crown size={14} /> MyPandaX VIP Program</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">VIP &amp; Badges</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-2xl">Four monthly tiers — Green, Purple and Black Panda VIP, then Panda Boss — unlocked by the Panda Coins you purchase each calendar month. Every tier resets on the 1st. Gifts you send are tracked separately and never change VIP progress.</p>
      </header>

      <VipArtwork src={tiers.artwork?.banner} alt="VIP Members — exclusive perks, special badges and more" file="vip_banner" aspect="aspect-[5/1]" fit="object-cover" />

      {user ? <VipProgress me={me} /> : (
        <Link to="/login" data-testid="vip-login-cta" className="block card-surface p-5 border border-white/8 text-sm text-zinc-300 hover:border-rose-500/50">Sign in to see your progress this month →</Link>
      )}

      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="text-base md:text-lg font-medium text-white">The four monthly tiers</h2>
          <Link to="/coins" data-testid="vip-top-up-link" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><Coins size={13} /> Buy coins</Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="vip-tier-grid">
          {rows.map((t, i) => <VipTierCard key={t.tier} t={t} index={i} current={me?.tier === t.tier} earned={!!curRank && t.rank <= curRank} />)}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4" data-testid="vip-benefits">
        <VipArtwork src={tiers.artwork?.benefits} alt="VIP Benefits — compare what you get with each VIP level" file="vip_benefits" aspect="aspect-[872/465]" fit="object-contain" className="bg-[#0d0d0d]" />
        <VipArtwork src={tiers.artwork?.boss_club} alt="Panda Boss Monthly Prestige Club" file="vip_bossclub" aspect="aspect-[872/425]" fit="object-contain" className="bg-[#0d0d0d]" />
      </section>

      <div data-testid="vip-reset-banner" className="border border-rose-500/40 bg-rose-950/20 p-4 flex items-center gap-4">
        <CalendarClock size={26} className="text-rose-400 shrink-0" />
        <div>
          <div className="font-display text-lg text-rose-300 uppercase tracking-wide">All VIP badges reset every month</div>
          <div className="text-sm text-zinc-400">Earn your status again each calendar month — progress counts verified coin purchases only.</div>
        </div>
      </div>

      <VipRules rules={tiers.rules} paused={tiers.paused} />
      <VipCashoutInfo cashout={tiers.cashout} />
    </div>
  );
}
